/* eslint-disable boundaries/element-types */
import PropTypes from 'prop-types';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import AuthContext from 'contexts/JWTContext';
import useNotification from 'hooks/useNotification';

import NotificationManager from './components/NotificationManager';
import { getNotificationGateway } from './realtime/NotificationGateway';
import { eventReceived } from './state/realtimeNotificationsSlice';

const NotificationContext = createContext();
const gateway = getNotificationGateway();

const defaultResolveUserChannel = (authState, loginUser) => {
  const authUserId = authState?.user?.id;
  if (authUserId) {
    return `user.${authUserId}`;
  }

  const fallbackUserId = loginUser?.id;
  if (fallbackUserId) {
    return `user.${fallbackUserId}`;
  }

  return null;
};

export const NotificationProvider = ({
  children,
  autoSubscribeUserChannel = true,
  enableDefaultToasts = true,
  resolveUserChannel = defaultResolveUserChannel,
}) => {
  const auth = useContext(AuthContext);
  const dispatch = useDispatch();
  const loginUser = useSelector((state) => state.login?.user);
  const notify = useNotification();

  const userChannel = useMemo(() => {
    if (typeof resolveUserChannel === 'function') {
      return resolveUserChannel(auth, loginUser);
    }
    return null;
  }, [auth, loginUser, resolveUserChannel]);

  const notificationManagerRef = useRef(null);

  useEffect(() => {
    return gateway.onAny((event) => {
      dispatch(
        eventReceived({
          channel: event.channel,
          event: event.event,
          type: event.type,
          payload: event.payload,
        }),
      );

      const normalizedEvent = (event.event || '').toLowerCase();

      if (!['notification', 'usernotificationsent'].includes(normalizedEvent)) {
        return;
      }

      const payload = event.payload || {};

      let rawMessage = payload?.message;

      if (!rawMessage && payload?.data) {
        let { data } = payload;

        if (typeof data === 'string') {
          try {
            data = JSON.parse(data);
          } catch (error) {
            data = null;
          }
        }

        if (data && typeof data === 'object') {
          rawMessage = data.message || rawMessage;
        }
      }

      if (typeof rawMessage === 'string') {
        try {
          rawMessage = JSON.parse(rawMessage);
        } catch (error) {
          rawMessage = null;
        }
      }

      const notificationMessage =
        rawMessage && typeof rawMessage === 'object' ? rawMessage : null;
      const messageType = notificationMessage?.type;
      if (messageType && notificationManagerRef.current) {
        notificationManagerRef.current.handleNotification({
          ...payload,
          message: notificationMessage,
        });
        return;
      }

      if (!enableDefaultToasts) {
        return;
      }

      const title =
        payload.title || payload.data?.title || payload.subject || '';
      const message =
        payload.message ||
        payload.data?.message ||
        payload.notification ||
        payload.data?.notification ||
        payload.body ||
        payload.data?.body;

      if (!message) {
        return;
      }

      const variant = payload.variant || payload.data?.variant || 'info';

      notify(message, true, title, variant);
    });
  }, [
    dispatch,
    gateway,
    notify,
    enableDefaultToasts,
    notificationManagerRef,
    auth,
    loginUser,
  ]);

  useEffect(() => {
    if (!autoSubscribeUserChannel) {
      return undefined;
    }

    if (!userChannel) {
      return undefined;
    }

    return gateway.subscribe({
      channel: userChannel,
      type: 'private',
      metadata: {
        scope: 'user',
      },
    });
  }, [gateway, autoSubscribeUserChannel, userChannel]);

  const register = useCallback(
    (subscription) => gateway.subscribe(subscription),
    [gateway],
  );

  const onAny = useCallback((handler) => gateway.onAny(handler), [gateway]);

  const value = useMemo(
    () => ({
      gateway,
      register,
      onAny,
      userChannel,
    }),
    [gateway, onAny, register, userChannel],
  );

  return (
    <NotificationContext.Provider value={value}>
      <NotificationManager ref={notificationManagerRef}>
        {children}
      </NotificationManager>
    </NotificationContext.Provider>
  );
};

NotificationProvider.propTypes = {
  children: PropTypes.node.isRequired,
  autoSubscribeUserChannel: PropTypes.bool,
  enableDefaultToasts: PropTypes.bool,
  resolveUserChannel: PropTypes.func,
};

export const useNotificationGateway = () => {
  const context = useContext(NotificationContext);

  if (!context) {
    throw new Error(
      'useNotificationGateway must be used within a NotificationProvider',
    );
  }

  return context;
};

export default NotificationContext;
